import { useEffect, useMemo, useRef, useState } from "react";
import type { WordDTO } from "@shared/types";
import { GameImage } from "../game/ui/GameImage";
import type { QuizQuestion as Q } from "./questionEngine";

/**
 * Controller answer UI for one Quizzle question. Shows the prompt + visual
 * answer options (audio plays on the HOST screen, never here). Submits once,
 * then locks. Calls onAnswer(correct, elapsedMs).
 */
export function QuizQuestion({
  question, questionStartedAt, onAnswer,
}: {
  question: Q;
  questionStartedAt: number;
  onAnswer: (correct: boolean, elapsedMs: number) => void;
}) {
  const [done, setDone] = useState(false);
  const submit = (correct: boolean) => {
    if (done) return;
    setDone(true);
    onAnswer(correct, Math.max(0, Date.now() - questionStartedAt));
  };

  if (done) {
    return (
      <div className="quizq-locked">
        <div className="quizq-locked-tick">✓</div>
        <p>Locked in!</p>
        <p className="quizzle-dim">Look up at the big screen.</p>
      </div>
    );
  }

  const isImagePrompt = question.kind === "image-to-word";
  const isAudioPrompt = question.kind === "audio-to-image" || question.kind === "audio-to-english";

  return (
    <div className="quizq">
      <div className="quizq-prompt">
        {isImagePrompt && <GameImage className="quizq-prompt-img" src={question.word.imageUrl} alt="" fallbackLabel="?" />}
        {isAudioPrompt && <div className="quizq-listen">🔊 Listen!</div>}
        {question.kind === "spell" && (
          <>
            {question.word.imageUrl && <GameImage className="quizq-prompt-img" src={question.word.imageUrl} alt="" fallbackLabel="?" />}
            <div className="quizq-listen quizq-listen-sm">🔊 Spell the word</div>
          </>
        )}
      </div>

      {question.kind === "spell"
        ? <SpellAnswer word={question.word} onSubmit={submit} />
        : <MultipleChoice question={question} onPick={submit} />}
    </div>
  );
}

function MultipleChoice({ question, onPick }: { question: Q; onPick: (correct: boolean) => void }) {
  const imageOptions = question.kind === "audio-to-image";
  return (
    <div className={`quizq-options ${imageOptions ? "is-image" : "is-text"}`}>
      {question.options.map((o: WordDTO) => (
        <button key={o.id} className="quizq-opt" onClick={() => onPick(o.id === question.word.id)}>
          {imageOptions
            ? <GameImage className="quizq-opt-img" src={o.imageUrl} alt={o.english} fallbackLabel={o.english[0] ?? "?"} />
            : <span className="quizq-opt-word">{o.english}</span>}
        </button>
      ))}
    </div>
  );
}

function SpellAnswer({ word, onSubmit }: { word: WordDTO; onSubmit: (correct: boolean) => void }) {
  const target = word.english.toUpperCase().replace(/[^A-Z]/g, "");
  const bank = useMemo(() => {
    const letters = target.split("");
    const pool = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").filter((c) => !target.includes(c));
    const extra = target.length >= 3 ? Math.min(2, 8 - letters.length) : 0;
    for (let i = 0; i < extra && pool.length; i++) letters.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]!);
    for (let i = letters.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [letters[i], letters[j]] = [letters[j]!, letters[i]!]; }
    return letters.map((ch, id) => ({ id, ch }));
  }, [target]);
  const [placed, setPlaced] = useState<({ tileId: number; ch: string } | null)[]>(() => target.split("").map(() => null));
  const usedRef = useRef<Set<number>>(new Set());
  usedRef.current = new Set(placed.filter(Boolean).map((p) => p!.tileId));

  const full = placed.every(Boolean);
  const tap = (t: { id: number; ch: string }) => {
    if (usedRef.current.has(t.id)) return;
    const idx = placed.findIndex((p) => p == null);
    if (idx < 0) return;
    setPlaced((prev) => prev.map((p, i) => (i === idx ? { tileId: t.id, ch: t.ch } : p)));
  };
  const clear = (idx: number) => { if (placed[idx]) setPlaced((prev) => prev.map((p, i) => (i === idx ? null : p))); };

  return (
    <div className="quizq-spell">
      <div className="spell-boxes">
        {placed.map((p, i) => (
          <div key={i} className={`spell-box ${p ? "filled" : ""}`} onClick={() => clear(i)}>
            {p ? <span className="spell-box-letter">{p.ch}</span> : null}
          </div>
        ))}
      </div>
      <div className="spell-bank">
        {bank.map((t) => (
          <button key={t.id} className="spell-tile" disabled={usedRef.current.has(t.id)}
            style={{ visibility: usedRef.current.has(t.id) ? "hidden" : "visible" }} onClick={() => tap(t)}>
            {t.ch}
          </button>
        ))}
      </div>
      <button className="btn btn-green btn-big" disabled={!full}
        onClick={() => onSubmit(placed.map((p) => p!.ch).join("") === target)}>
        Submit
      </button>
    </div>
  );
}
